import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddBoxIcon from '@mui/icons-material/AddBox';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardHeader from '../components/common/DashboardHeader';
import DashboardReportHeader from '../components/common/DashboardReportHeader';
import NotchTabs from '../components/common/NotchTabs';
import StockAddInventorySection from '../components/stock/StockAddInventorySection';
import StockInventoryListSection from '../components/stock/StockInventoryListSection';
import StockRawMaterialSection from '../components/stock/StockRawMaterialSection';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StockOutSection from '../components/stock/StockOutSection';
import StockPoolInventoryDashboard from '../components/stock/StockPoolInventoryDashboard';
import { AsyncValue, ChartAreaSkeleton } from '../components/stock/StockDashboardPartials';
import StockReportsSection from '../components/stock/StockReportsSection';
import StockSupplierSection from '../components/stock/StockSupplierSection';
import { alpha } from '@mui/material/styles';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import {
  getDashboardPageShellSx,
  getDashboardDarkSurfaceTokens,
} from '../utils/dashboardSurfaceSx';
import {
  getStockChartCardSx,
  getStockChartPlotSx,
  getStockChartMargin,
  getStockLegendProps,
  getStockDashboardSectionSx,
  getStockInnerPanelSx,
  getStockTabsPaperSx,
  getStockThemeColors,
  STOCK_ACCENT,
} from '../utils/stockMobileLayout';
import { THEME_COLORS } from '../shared/constants/theme';
import { getStockDashboardStats } from '../services/api';
import moment from 'moment';
import {
  getInsightsChartPrimaryGridProps,
  getInsightsChartSecondaryGridProps,
} from '../utils/insightsDashboardLayout';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const CUSTOM_BORDER_COLOR = STOCK_ACCENT;

const STOCK_MANAGEMENT_TABS = [
  { icon: DashboardIcon, label: 'Dashboard', shortLabel: 'Home' },
  { icon: FormatListBulletedIcon, label: 'Inventory', shortLabel: 'List' },
  { icon: AddBoxIcon, label: 'Stock In', shortLabel: 'In' },
  { icon: ImportExportIcon, label: 'Stock Movement', shortLabel: 'Move' },
  { icon: LocalShippingIcon, label: 'Suppliers', shortLabel: 'Supp' },
  { icon: CategoryOutlinedIcon, label: 'Products / Items', shortLabel: 'Items' },
  { icon: AssessmentIcon, label: 'Reports', shortLabel: 'Rpt' },
];
const LOW_STOCK_THRESHOLD = 20;
const UPDATE_COMPLETENESS_THRESHOLD = 70;
/** Debounce date/month changes after the first successful load (ms). */
const DATE_REFETCH_DEBOUNCE_MS = 320;
const ROOMNEXA_LOGO_URL = '/static/topbar/logo_ed_dark.png';

const loadRoomnexaLogoDataUrl = async () => {
  try {
    const response = await fetch(ROOMNEXA_LOGO_URL);
    if (!response.ok) return null;
    const logoBlob = await response.blob();
    const objectUrl = URL.createObjectURL(logoBlob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || 300;
      canvas.height = image.naturalHeight || 87;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    return null;
  }
};

const InventoryOperationsDashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [startDate, setStartDate] = useState(moment().startOf('month'));
  const [endDate, setEndDate] = useState(moment().endOf('month'));
  const [currentMonth, setCurrentMonth] = useState(moment());
  const activeStockTab = searchParams.get('tab');
  const isDashboardTab = !activeStockTab;

  const [statistics, setStatistics] = useState({});
  const [rangeDailyStats, setRangeDailyStats] = useState([]);
  const [monthlyDailyStats, setMonthlyDailyStats] = useState([]);
  const [topPurchaseProductsApi, setTopPurchaseProductsApi] = useState([]);
  const [stockSnapshotApi, setStockSnapshotApi] = useState([]);
  const [loading, setLoading] = useState(true);
  /** True while refetching after first paint — shows slim progress without hiding dashboard */
  const [refreshing, setRefreshing] = useState(false);
  const hasCompletedFetchRef = useRef(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [poolInventory, setPoolInventory] = useState(null);
  const [error, setError] = useState(null);
  const [pdfExporting, setPdfExporting] = useState(false);
  const reportRef = useRef(null);
  const exportInProgressRef = useRef(false);

  const fetchDashboardData = useCallback(async ({ manualRefresh = false } = {}) => {
    if (!isDashboardTab) return;
    if (!startDate?.isValid?.() || !endDate?.isValid?.() || !currentMonth?.isValid?.()) return;
    if (startDate.isAfter(endDate)) {
      setError('Start date must be before or equal to end date.');
      return;
    }

    const isRepeat = hasCompletedFetchRef.current;
    setError(null);
    if (isRepeat || manualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await getStockDashboardStats({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        month: currentMonth.format('YYYY-MM'),
      });

      setStatistics(response?.statistics || {});
      setRangeDailyStats(response?.range_daily_stats || []);
      setMonthlyDailyStats(response?.monthly_daily_stats || []);
      setTopPurchaseProductsApi(response?.top_purchase_products || []);
      setStockSnapshotApi(response?.stock_snapshot || []);
      setPoolInventory(response?.pool_inventory || null);
    } catch (err) {
      setError('Failed to load latest stock dashboard data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
      hasCompletedFetchRef.current = true;
      setHasLoadedOnce(true);
    }
  }, [currentMonth, endDate, isDashboardTab, startDate]);

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const isDark = currentTheme === 'dark';
  const themeColors = getStockThemeColors(isDark);
  const surfaceTokens = getDashboardDarkSurfaceTokens(isDark);

  useEffect(() => {
    if (!isDashboardTab) {
      setLoading(false);
      setRefreshing(false);
      return undefined;
    }

    const debounceMs = hasCompletedFetchRef.current ? DATE_REFETCH_DEBOUNCE_MS : 0;
    const timer = window.setTimeout(() => {
      fetchDashboardData();
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [fetchDashboardData, isDashboardTab]);

  const rangeLabel = `${startDate.format('DD MMM YYYY')} - ${endDate.format('DD MMM YYYY')}`;
  const monthRangeLabel = `${currentMonth.clone().startOf('month').format('DD MMM YYYY')} - ${currentMonth.clone().endOf('month').format('DD MMM YYYY')}`;

  const handleDateRangeChange = (nextStartDate, nextEndDate) => {
    if (nextStartDate?.isValid?.() && nextEndDate?.isValid?.()) {
      setStartDate(nextStartDate);
      setEndDate(nextEndDate);
      setCurrentMonth(nextStartDate.clone());
    }
  };

  const handleDownloadPdf = async () => {
    if (loading || pdfExporting || exportInProgressRef.current || !reportRef.current) return;
    exportInProgressRef.current = true;
    document.body.classList.add('dashboard-pdf-exporting');
    setPdfExporting(true);
    try {
      const filename = `stock-dashboard-${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}.pdf`;
      const exportRangeLabel = `${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')}`;

      await new Promise((resolve) => setTimeout(resolve, 120));
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const logoDataUrl = await loadRoomnexaLogoDataUrl();
      const exportBgColor = isDark ? THEME_COLORS.BACKGROUND_DARK : '#ffffff';
      const reportElement = reportRef.current;
      const previousReportBg = reportElement.style.backgroundColor;
      reportElement.style.backgroundColor = exportBgColor;

      let canvas;
      try {
        canvas = await html2canvas(reportElement, {
          backgroundColor: exportBgColor,
          scale: Math.max(2, window.devicePixelRatio || 1),
          useCORS: true,
          allowTaint: true,
          logging: false,
          windowWidth: reportElement.scrollWidth,
          windowHeight: reportElement.scrollHeight,
          onclone: (documentClone, clonedElement) => {
            documentClone.body.style.backgroundColor = exportBgColor;
            if (clonedElement) {
              clonedElement.style.backgroundColor = exportBgColor;
            }
          },
        });
      } finally {
        reportElement.style.backgroundColor = previousReportBg;
      }

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 28;
      const footerHeight = 13;
      const imageTop = headerHeight + margin;
      const printableHeight = pageHeight - imageTop - footerHeight - margin;
      const maxImageWidth = pageWidth - margin * 2;
      let pageIndex = 1;

      const drawPdfHeader = () => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, headerHeight, 'F');
        if (logoDataUrl) {
          pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
        } else {
          pdf.setTextColor(30, 41, 59);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(18);
          pdf.text('RoomNexa', pageWidth / 2, 8.5, { align: 'center' });
        }
        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Stock Management Dashboard Report', pageWidth / 2, 18.6, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(
          `Range: ${exportRangeLabel} | Generated: ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${pageIndex}`,
          pageWidth / 2,
          23.6,
          { align: 'center' }
        );
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
      };

      const drawPdfFooter = () => {
        const footerTop = pageHeight - footerHeight;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, footerTop, pageWidth, footerHeight, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, footerTop, pageWidth - margin, footerTop);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Powered by RoomNexa', margin, footerTop + 5.2);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6.8);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Professional hotel stock intelligence for smarter operations.', margin, footerTop + 9.2);
        pdf.text(`Page ${pageIndex}`, pageWidth - margin, footerTop + 7.4, { align: 'right' });
      };

      const imageData = canvas.toDataURL('image/png');
      const imageWidth = maxImageWidth;
      const imageHeight = (canvas.height * imageWidth) / canvas.width;
      const imageLeft = (pageWidth - imageWidth) / 2;
      let renderedHeight = 0;
      while (renderedHeight < imageHeight) {
        drawPdfHeader();
        pdf.addImage(imageData, 'PNG', imageLeft, imageTop - renderedHeight, imageWidth, imageHeight);
        drawPdfFooter();
        renderedHeight += printableHeight;
        if (renderedHeight < imageHeight) {
          pdf.addPage();
          pageIndex += 1;
        }
      }

      pdf.save(filename);
    } finally {
      document.body.classList.remove('dashboard-pdf-exporting');
      setPdfExporting(false);
      exportInProgressRef.current = false;
    }
  };

  const hasStockActivityForDay = (dayData) => (
    Boolean(dayData)
    && (
      Number(dayData?.booked || 0) > 0
      || Number(dayData?.direct_booked || 0) > 0
      || Number(dayData?.online_booked || 0) > 0
      || Number(dayData?.cancelled || 0) > 0
      || Number(dayData?.movement || 0) > 0
      || Number(dayData?.inward || 0) > 0
      || Number(dayData?.outward || 0) > 0
      || Number(dayData?.total_expense || 0) > 0
      || Number(dayData?.restaurant_purchase || 0) > 0
    )
  );

  const buildStockTimeline = (statsSource, timelineStart, timelineEnd) => {
    if (!timelineStart?.isValid?.() || !timelineEnd?.isValid?.()) return [];
    const dayMap = {};
    statsSource.forEach((stat) => {
      const key = moment(stat?.date).format('YYYY-MM-DD');
      if (key !== 'Invalid date') dayMap[key] = stat;
    });

    const timeline = [];
    const pointer = timelineStart.clone().startOf('day');
    const end = timelineEnd.clone().startOf('day');

    while (pointer.isSameOrBefore(end)) {
      const key = pointer.format('YYYY-MM-DD');
      const dayData = dayMap[key];
      const hasActivity = hasStockActivityForDay(dayData);
      const statusValueRaw = Number(
        dayData?.statusValue
        ?? dayData?.completeness
        ?? (hasActivity ? 100 : 0)
      );
      const statusValue = Math.min(100, Math.max(0, Math.round(statusValueRaw)));
      const totalStock = Number(dayData?.total ?? 0);
      const computedClosingStock = Number(dayData?.closingStock ?? dayData?.total ?? dayData?.not_booked ?? 0);
      const closingStock = Number.isFinite(computedClosingStock) ? computedClosingStock : 0;
      const openingStock = Number(dayData?.openingStock ?? closingStock);
      const availableStock = Number(dayData?.not_booked ?? closingStock);
      const inward = Number(dayData?.inward ?? dayData?.movement ?? 0);
      const outward = Number(dayData?.outward ?? 0);
      const movement = Number(dayData?.movement ?? (inward + outward));
      timeline.push({
        key,
        dateLabel: pointer.format('DD MMM'),
        isUpdated: statusValue >= UPDATE_COMPLETENESS_THRESHOLD,
        statusValue,
        openingStock,
        inward,
        outward,
        closingStock,
        movement,
        restaurantPurchase: Number(dayData?.restaurant_purchase || 0),
        booked: dayData?.booked || 0,
        available: availableStock,
        cancelled: dayData?.cancelled || 0,
        direct_booked: dayData?.direct_booked || 0,
        online_booked: dayData?.online_booked || 0,
        total_expense: Number(dayData?.total_expense || 0),
      });
      pointer.add(1, 'day');
    }

    return timeline;
  };

  const stockClosingTimeline = useMemo(
    () => buildStockTimeline(rangeDailyStats, startDate, endDate),
    [endDate, rangeDailyStats, startDate]
  );

  const closingTrackerTimeline = useMemo(
    () => buildStockTimeline(
      monthlyDailyStats,
      currentMonth.clone().startOf('month'),
      currentMonth.clone().endOf('month')
    ),
    [currentMonth, monthlyDailyStats]
  );

  const monthProgressDays = useMemo(() => {
    const monthMap = {};
    monthlyDailyStats.forEach((stat) => {
      const key = moment(stat?.date).format('YYYY-MM-DD');
      if (key !== 'Invalid date') monthMap[key] = stat;
    });

    const days = [];
    const monthStart = currentMonth.clone().startOf('month');
    const monthEnd = currentMonth.clone().endOf('month');
    const cursor = monthStart.clone();

    while (cursor.isSameOrBefore(monthEnd)) {
      const key = cursor.format('YYYY-MM-DD');
      const dayData = monthMap[key];
      const statusValue = Number(dayData?.statusValue ?? dayData?.completeness ?? 0);
      days.push({
        key,
        day: cursor.format('D'),
        isUpdated: statusValue >= UPDATE_COMPLETENESS_THRESHOLD,
      });
      cursor.add(1, 'day');
    }

    return days;
  }, [monthlyDailyStats, currentMonth]);

  const totalDays = stockClosingTimeline.length;
  const updatedDays = stockClosingTimeline.filter((day) => Number(day.statusValue || 0) >= UPDATE_COMPLETENESS_THRESHOLD).length;
  const missedDays = totalDays - updatedDays;
  const updateAccuracy = totalDays
    ? Math.round(
      stockClosingTimeline.reduce((sum, day) => sum + Number(day.statusValue || 0), 0) / totalDays
    )
    : 0;

  const monthUpdatedDays = monthProgressDays.filter((day) => day.isUpdated).length;
  const monthMissedDays = monthProgressDays.length - monthUpdatedDays;
  const closingTrackerTotalDays = closingTrackerTimeline.length;
  const closingTrackerUpdatedDays = closingTrackerTimeline.filter(
    (day) => Number(day.statusValue || 0) >= UPDATE_COMPLETENESS_THRESHOLD
  ).length;
  const closingTrackerMissedDays = closingTrackerTotalDays - closingTrackerUpdatedDays;
  const closingTrackerUpdateAccuracy = closingTrackerTotalDays
    ? Math.round(
      closingTrackerTimeline.reduce((sum, day) => sum + Number(day.statusValue || 0), 0)
        / closingTrackerTotalDays
    )
    : 0;

  const monthLowStockTrendData = useMemo(() => (
    monthlyDailyStats.map((point) => {
      const pointDate = moment(point?.date);
      const availableStock = Number(point?.not_booked ?? point?.closingStock ?? point?.total ?? 0);
      return {
        name: pointDate.isValid() ? pointDate.format('DD MMM') : '',
        low_stock_signal: availableStock < LOW_STOCK_THRESHOLD
          ? LOW_STOCK_THRESHOLD - availableStock
          : 0,
        wastage: Number(point?.cancelled || 0),
      };
    })
  ), [monthlyDailyStats]);

  const dailyExpenseTrendData = useMemo(
    () => stockClosingTimeline.map((point) => ({
      name: point.dateLabel,
      expense: Number(point.total_expense || 0),
      restaurantPurchase: Number(point.restaurantPurchase || 0),
      stock: Number(point.closingStock || 0),
    })),
    [stockClosingTimeline]
  );

  const monthDailyExpenseTrendData = useMemo(
    () => closingTrackerTimeline.map((point) => ({
      name: point.dateLabel,
      expense: Number(point.total_expense || 0),
      restaurantPurchase: Number(point.restaurantPurchase || 0),
      stock: Number(point.closingStock || 0),
    })),
    [closingTrackerTimeline]
  );

  const topPurchaseProducts = useMemo(() => {
    if (Array.isArray(topPurchaseProductsApi) && topPurchaseProductsApi.length) {
      return topPurchaseProductsApi.slice(0, 6).map((item, index) => ({
        id: item.id || `${item.name || item.product || 'product'}-${index}`,
        name: item.name || item.product || `Product ${index + 1}`,
        quantity: Number(item.quantity ?? item.qty ?? item.purchased_qty ?? 0),
        amount: Number(item.amount ?? item.value ?? item.total ?? 0),
      }));
    }
    return [];
  }, [topPurchaseProductsApi]);

  const currentStockSnapshot = useMemo(() => {
    if (Array.isArray(stockSnapshotApi) && stockSnapshotApi.length) {
      return stockSnapshotApi.slice(0, 6).map((item, index) => ({
        id: `${item.name || 'stock'}-${index}`,
        name: item.name || `Type ${index + 1}`,
        available: Number(item.available || 0),
        total: Number(item.total || 0),
      }));
    }
    return [{
      id: 'overall',
      name: 'Overall Current Stock',
      available: Number(statistics?.not_booked_inventory || 0),
      total: Number(statistics?.total_inventory || 0),
    }];
  }, [stockSnapshotApi, statistics]);

  const chartPalette = useMemo(() => ([
    themeColors.cyan,
    themeColors.info,
    themeColors.warning,
    themeColors.purple,
    themeColors.success,
    themeColors.booked,
  ]), [
    themeColors.booked,
    themeColors.cyan,
    themeColors.info,
    themeColors.purple,
    themeColors.success,
    themeColors.warning,
  ]);

  const topPurchaseProductsChartData = useMemo(() => {
    const source = topPurchaseProducts
      .map((item) => {
        const quantity = Number(item.quantity || 0);
        const amount = Number(item.amount || 0);
        return {
          ...item,
          value: quantity > 0 ? quantity : amount,
        };
      })
      .filter((item) => Number(item.value || 0) > 0);

    const total = source.reduce((sum, item) => sum + Number(item.value || 0), 0);
    return source.map((item) => ({
      ...item,
      percentage: total > 0 ? (Number(item.value || 0) / total) * 100 : 0,
    }));
  }, [topPurchaseProducts]);

  const currentStockSnapshotChartData = useMemo(() => (
    currentStockSnapshot.map((item) => {
      const total = Number(item.total || 0);
      const current_qty = Number(item.available || 0);
      return {
        id: item.id,
        name: item.name,
        current_qty,
        total,
      };
    })
  ), [currentStockSnapshot]);

  const stockInsights = useMemo(() => {
    const avgDailyExpense = dailyExpenseTrendData.length
      ? (dailyExpenseTrendData.reduce((sum, row) => sum + Number(row.expense || 0), 0) / dailyExpenseTrendData.length)
      : 0;
    const avgDailyRestaurantPurchase = dailyExpenseTrendData.length
      ? (dailyExpenseTrendData.reduce((sum, row) => sum + Number(row.restaurantPurchase || 0), 0) / dailyExpenseTrendData.length)
      : 0;
    const avgDailyStock = dailyExpenseTrendData.length
      ? (dailyExpenseTrendData.reduce((sum, row) => sum + Number(row.stock || 0), 0) / dailyExpenseTrendData.length)
      : 0;
    return {
      avgDailyExpense,
      avgDailyRestaurantPurchase,
      avgDailyStock,
      projectedMonthlyExpense: avgDailyExpense * 30,
      projectedMonthlyRestaurantPurchase: avgDailyRestaurantPurchase * 30,
    };
  }, [dailyExpenseTrendData]);

  const monthStockInsights = useMemo(() => {
    const avgDailyExpense = monthDailyExpenseTrendData.length
      ? (monthDailyExpenseTrendData.reduce((sum, row) => sum + Number(row.expense || 0), 0) / monthDailyExpenseTrendData.length)
      : 0;
    const avgDailyRestaurantPurchase = monthDailyExpenseTrendData.length
      ? (monthDailyExpenseTrendData.reduce((sum, row) => sum + Number(row.restaurantPurchase || 0), 0) / monthDailyExpenseTrendData.length)
      : 0;
    const avgDailyStock = monthDailyExpenseTrendData.length
      ? (monthDailyExpenseTrendData.reduce((sum, row) => sum + Number(row.stock || 0), 0) / monthDailyExpenseTrendData.length)
      : 0;
    return {
      avgDailyExpense,
      avgDailyRestaurantPurchase,
      avgDailyStock,
      projectedMonthlyExpense: monthDailyExpenseTrendData.reduce(
        (sum, row) => sum + Number(row.expense || 0),
        0
      ),
      projectedMonthlyRestaurantPurchase: monthDailyExpenseTrendData.reduce(
        (sum, row) => sum + Number(row.restaurantPurchase || 0),
        0
      ),
    };
  }, [monthDailyExpenseTrendData]);

  const totalExpenseInRange = useMemo(
    () => dailyExpenseTrendData.reduce((sum, row) => sum + Number(row.expense || 0), 0),
    [dailyExpenseTrendData]
  );

  const totalRestaurantPurchaseInRange = useMemo(
    () => dailyExpenseTrendData.reduce((sum, row) => sum + Number(row.restaurantPurchase || 0), 0),
    [dailyExpenseTrendData]
  );

  const dashboardSectionSx = getStockDashboardSectionSx();

  const chartCardSx = (accentColor) => getStockChartCardSx(isDark, themeColors, accentColor);

  const chartPlotSx = (height) => getStockChartPlotSx(isDark, themeColors, height);
  const chartMarginLegend = getStockChartMargin({ withLegend: true });
  const chartLegendProps = getStockLegendProps(themeColors);

  const showClosingPanel = true;
  const showExpensePanel = true;
  const showProductsPanel = true;
  const showAlertsPanel = true;
  const dashboardBusy = loading || refreshing;

  const TAB_ROUTES = [
    '/stock/',
    '/stock/?tab=stock-list',
    '/stock/?tab=add-stock',
    '/stock/?tab=stock-movement',
    '/stock/?tab=suppliers',
    '/stock/?tab=raw-materials',
    '/stock/?tab=reports',
  ];

  const activeTab = useMemo(() => {
    const map = {
      'stock-list': 1,
      'inventory-list': 1,
      'add-stock': 2,
      'add-inventory': 2,
      'stock-out': 3,
      'stock-movement': 3,
      adjustment: 3,
      transfer: 3,
      suppliers: 4,
      'raw-materials': 5,
      reports: 6,
    };
    if (!activeStockTab) return 0;
    return map[activeStockTab] ?? 0;
  }, [activeStockTab]);

  const handleTabChange = (newValue) => {
    navigate(TAB_ROUTES[newValue] || '/stock/');
  };

  const inventoryMode = searchParams.get('mode') || 'add';
  const inventoryIdParam = searchParams.get('id');
  const selectedInventoryId = inventoryIdParam ? Number(inventoryIdParam) : null;

  const renderClosingStockTooltip = ({ active, payload, label }) => {
    if (!active || !Array.isArray(payload) || !payload.length) return null;
    const point = payload[0]?.payload || {};
    const openingStock = Number(point.openingStock || 0);
    const inward = Number(point.inward || 0);
    const outward = Number(point.outward || 0);
    const closingStock = Number(point.closingStock || 0);
    const statusValue = Number(point.statusValue || 0);

    return (
      <Paper
        elevation={4}
        sx={{
          p: 1.2,
          minWidth: 220,
          background: surfaceTokens.surfaceGradient,
          border: `1px solid ${surfaceTokens.surfaceBorder}`,
        }}
      >
        <Typography sx={{ fontSize: '0.78rem', color: themeColors.subtleText, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', color: themeColors.text }}>
          Opening: {openingStock.toLocaleString('en-IN')}
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', color: themeColors.success }}>
          Inward: +{inward.toLocaleString('en-IN')}
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', color: themeColors.danger }}>
          Outward: -{outward.toLocaleString('en-IN')}
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', color: themeColors.text, fontWeight: 700, mt: 0.4 }}>
          Closing: {closingStock.toLocaleString('en-IN')}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: themeColors.info, mt: 0.55 }}>
          Completeness: {statusValue.toFixed(0)}%
        </Typography>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        ...getDashboardPageShellSx(currentTheme),
        minHeight: '100vh',
      }}
    >
      <Box sx={{ mb: 3 }}>
        <DashboardHeader
          title="Stock Management Dashboard"
          breadcrumbs={[
            { label: 'Home', path: '/dashboard/' },
            { label: 'Stock Management' },
          ]}
          navLinks={[
            { url: '/stock/?tab=stock-list', label: 'Inventory' },
            { url: '/stock/?tab=add-stock', label: 'Stock In' },
          ]}
        />
      </Box>

      <Paper
        elevation={0}
        sx={getStockTabsPaperSx(isDark)}
      >
        <NotchTabs
          value={activeTab}
          onChange={(_, newValue) => handleTabChange(newValue)}
          tabs={STOCK_MANAGEMENT_TABS}
          accentColor={CUSTOM_BORDER_COLOR}
          mobileRows={3}
          sx={{ width: '100%', m: 0 }}
        />
      </Paper>

      {activeTab === 0 && (
        <>
      <DashboardReportHeader
        title="Stock dashboard insights"
        subtitle="Hotel and restaurant inventory, purchases, stock movement and alerts in one view."
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        onRefresh={() => fetchDashboardData({ manualRefresh: true })}
        onPdfDownload={handleDownloadPdf}
        loading={loading || refreshing}
        pdfExporting={pdfExporting}
        pdfDisabled={!hasLoadedOnce}
        themeMode={currentTheme}
        accentColor={STOCK_ACCENT}
      />

      {refreshing && (
        <LinearProgress
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1100,
            mb: 0,
            '& .MuiLinearProgress-bar': {
              transition: 'transform 0.2s linear',
            },
          }}
        />
      )}

      {error && (
        <Box sx={{ mb: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      <Box
        ref={reportRef}
        sx={{
          backgroundColor: currentTheme === 'light' ? '#f2f4f7' : 'transparent',
        }}
      >
      {!error && (
        <>
          <Box sx={dashboardSectionSx}>
            <StockPoolInventoryDashboard
              poolInventory={poolInventory}
              isDark={isDark}
              themeColors={themeColors}
              rangeLabel={rangeLabel}
              loading={dashboardBusy}
            />
          </Box>

          {showClosingPanel && (
            <Box sx={dashboardSectionSx}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={chartCardSx(themeColors.info)}
                >
                <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 600, mb: 0.5 }}>
                  Daily Stock Closing Tracker
                </Typography>
                <Typography variant="body2" sx={{ color: themeColors.subtleText, mb: 2 }}>
                  Track stock closing day by day and monitor compliance for {monthRangeLabel}
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item {...getInsightsChartSecondaryGridProps()}>
                    <Paper
                      elevation={0}
                      sx={{
                        ...getStockInnerPanelSx(isDark, themeColors),
                        height: '100%',
                      }}
                    >
                      <AsyncValue loading={dashboardBusy} isDark={isDark} skeletonHeight={38} skeletonWidth="35%">
                        <Typography sx={{ color: themeColors.text, fontWeight: 700, fontSize: '1.6rem', lineHeight: 1.2 }}>
                          {closingTrackerUpdateAccuracy}%
                        </Typography>
                      </AsyncValue>
                      <Typography sx={{ color: themeColors.subtleText, fontSize: '0.82rem', mb: 1.25 }}>
                        Update Accuracy
                      </Typography>
                      <AsyncValue loading={dashboardBusy} isDark={isDark} skeletonHeight={18} skeletonWidth="80%">
                        <Typography sx={{ color: themeColors.subtleText, fontSize: '0.78rem', mb: 0.6 }}>
                          Closing stock updated on {closingTrackerUpdatedDays} of {closingTrackerTotalDays} days
                        </Typography>
                      </AsyncValue>
                      {dashboardBusy ? (
                        <ChartAreaSkeleton isDark={isDark} height={8} sx={{ mb: 1.2, borderRadius: 4 }} />
                      ) : (
                        <LinearProgress
                          variant="determinate"
                          value={closingTrackerUpdateAccuracy}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            mb: 1.2,
                            backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: closingTrackerUpdateAccuracy >= 75 ? themeColors.success : themeColors.danger,
                            },
                          }}
                        />
                      )}
                      <AsyncValue loading={dashboardBusy} isDark={isDark} skeletonHeight={18} skeletonWidth="45%">
                        <Typography sx={{ color: themeColors.danger, fontSize: '0.8rem', fontWeight: 600 }}>
                          {closingTrackerMissedDays} days missed
                        </Typography>
                      </AsyncValue>
                    </Paper>
                  </Grid>

                  <Grid item {...getInsightsChartPrimaryGridProps()}>
                    <Paper
                      elevation={0}
                      sx={getStockInnerPanelSx(isDark, themeColors)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2, flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ color: themeColors.text, fontWeight: 600, fontSize: '0.92rem' }}>
                          {currentMonth.format('MMMM YYYY')} Progress ({monthUpdatedDays}/{monthProgressDays.length} days)
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton onClick={() => setCurrentMonth((prev) => prev.clone().subtract(1, 'month'))} size="small" sx={{ color: themeColors.text }}>
                            <ArrowBackIosIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={() => setCurrentMonth((prev) => prev.clone().add(1, 'month'))} size="small" sx={{ color: themeColors.text }}>
                            <ArrowForwardIosIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(10, minmax(0, 1fr))', gap: 0.75 }}>
                        {dashboardBusy ? (
                          Array.from({ length: 30 }).map((_, index) => (
                            <ChartAreaSkeleton key={`day-skeleton-${index}`} isDark={isDark} height={28} sx={{ borderRadius: 1 }} />
                          ))
                        ) : (
                          monthProgressDays.map((day) => (
                            <Box
                              key={day.key}
                              sx={{
                                textAlign: 'center',
                                borderRadius: 1,
                                py: 0.5,
                                border: `1px solid ${day.isUpdated ? 'rgba(76,225,182,0.35)' : 'rgba(239,83,80,0.35)'}`,
                                backgroundColor: day.isUpdated
                                  ? (isDark ? 'rgba(76,225,182,0.08)' : 'rgba(76,225,182,0.06)')
                                  : (isDark ? 'rgba(239,83,80,0.08)' : 'rgba(239,83,80,0.06)'),
                                color: day.isUpdated ? themeColors.success : themeColors.danger,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                              }}
                            >
                              {day.day}
                            </Box>
                          ))
                        )}
                      </Box>
                      <AsyncValue loading={dashboardBusy} isDark={isDark} skeletonHeight={16} skeletonWidth="40%">
                        <Typography sx={{ color: themeColors.subtleText, fontSize: '0.74rem', mt: 1 }}>
                          Missed this month: {monthMissedDays} days
                        </Typography>
                      </AsyncValue>
                    </Paper>
                  </Grid>
                </Grid>

                  {dashboardBusy ? (
                    <ChartAreaSkeleton isDark={isDark} height={290} />
                  ) : (
                  <Box sx={chartPlotSx(290)}>
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={290}>
                      <ComposedChart data={closingTrackerTimeline} margin={chartMarginLegend}>
                        <CartesianGrid strokeDasharray="3 3" stroke={themeColors.surfaceBorder} opacity={isDark ? 0.2 : 0.3} />
                        <XAxis dataKey="dateLabel" tick={{ fontSize: 11, fill: themeColors.text }} stroke={themeColors.surfaceBorder} />
                        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: themeColors.text }} stroke={themeColors.surfaceBorder} tickFormatter={(value) => Number(value).toLocaleString('en-IN')} />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: themeColors.text }} stroke={themeColors.surfaceBorder} tickFormatter={(value) => `${value}%`} />
                        <Tooltip content={renderClosingStockTooltip} />
                        <Legend {...chartLegendProps} />
                        <Bar yAxisId="left" dataKey="closingStock" name="Closing Stock">
                          {closingTrackerTimeline.map((entry) => (
                            <Cell key={entry.key} fill={entry.isUpdated ? themeColors.success : themeColors.danger} />
                          ))}
                        </Bar>
                        <Line yAxisId="right" type="monotone" dataKey="statusValue" name="Update Completeness %" stroke={themeColors.info} strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
            </Box>
          )}

          {showExpensePanel && (
            <Box sx={dashboardSectionSx}>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} lg={8}>
                <Paper
                  elevation={0}
                  sx={chartCardSx(themeColors.warning)}
                >
                <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 600, mb: 0.5 }}>
                  Hotel & restaurant purchases
                </Typography>
                <Typography variant="body2" sx={{ color: themeColors.subtleText, mb: 1.8 }}>
                  Day-wise hotel purchase total vs restaurant purchase total ({monthRangeLabel})
                </Typography>
                {dashboardBusy ? (
                  <ChartAreaSkeleton isDark={isDark} height={300} />
                ) : (
                <Box sx={chartPlotSx(300)}>
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={300}>
                    <LineChart data={monthDailyExpenseTrendData} margin={chartMarginLegend}>
                      <CartesianGrid strokeDasharray="3 3" stroke={themeColors.surfaceBorder} opacity={isDark ? 0.2 : 0.3} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: themeColors.text }} stroke={themeColors.surfaceBorder} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11, fill: themeColors.text }} stroke={themeColors.surfaceBorder} tickFormatter={(value) => `₹${Math.round(Number(value || 0)).toLocaleString('en-IN')}`} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: themeColors.text }} stroke={themeColors.surfaceBorder} tickFormatter={(value) => `₹${Math.round(Number(value || 0)).toLocaleString('en-IN')}`} />
                      <Tooltip />
                      <Legend {...chartLegendProps} />
                      <Line yAxisId="left" type="monotone" dataKey="expense" name="Hotel purchases" stroke={themeColors.warning} strokeWidth={2.4} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="restaurantPurchase" name="Restaurant purchase" stroke={themeColors.info} strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
                )}
                </Paper>
              </Grid>
              <Grid item xs={12} lg={4}>
                <Paper
                  elevation={0}
                  sx={chartCardSx(themeColors.cyan)}
                >
                <Typography sx={{ color: themeColors.text, fontWeight: 700, mb: 1.1 }}>
                  Current Stock Insights
                </Typography>
                <Typography sx={{ color: themeColors.subtleText, fontSize: '0.82rem', mb: 1.8 }}>
                  Advanced demand and cost indicators for the current month
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  {[
                    { label: 'Avg daily hotel purchases', value: `₹${Number(monthStockInsights.avgDailyExpense || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` },
                    { label: 'Avg daily restaurant purchase', value: `₹${Number(monthStockInsights.avgDailyRestaurantPurchase || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` },
                    { label: 'Projected monthly restaurant purchase', value: `₹${Number(monthStockInsights.projectedMonthlyRestaurantPurchase || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` },
                    { label: 'Projected monthly hotel purchases', value: `₹${Number(monthStockInsights.projectedMonthlyExpense || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` },
                  ].map((insight) => (
                    <Box key={insight.label} sx={{ p: 1.2, borderRadius: 1.5, border: `1px solid ${themeColors.surfaceBorder}` }}>
                      <Typography sx={{ color: themeColors.subtleText, fontSize: '0.75rem' }}>{insight.label}</Typography>
                      <AsyncValue loading={dashboardBusy} isDark={isDark} skeletonHeight={22} skeletonWidth="55%">
                        <Typography sx={{ color: themeColors.text, fontWeight: 700 }}>
                          {insight.value}
                        </Typography>
                      </AsyncValue>
                    </Box>
                  ))}
                </Box>
                </Paper>
              </Grid>
            </Grid>
            </Box>
          )}

          {showProductsPanel && (
            <Box sx={dashboardSectionSx}>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={chartCardSx(themeColors.purple)}
                >
                <Typography sx={{ color: themeColors.text, fontWeight: 700, mb: 0.4 }}>
                  Top Purchase Products
                </Typography>
                <Typography sx={{ color: themeColors.subtleText, fontSize: '0.8rem', mb: 1.5 }}>
                  Highest contributing products/categories in current period
                </Typography>
                {dashboardBusy ? (
                  <ChartAreaSkeleton isDark={isDark} height={300} />
                ) : topPurchaseProductsChartData.length ? (
                  <Box sx={chartPlotSx(300)}>
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={300}>
                      <PieChart margin={chartMarginLegend}>
                        <Pie
                          data={topPurchaseProductsChartData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={52}
                          outerRadius={88}
                          paddingAngle={2}
                          stroke={themeColors.surface}
                          strokeWidth={2}
                        >
                          {topPurchaseProductsChartData.map((entry, index) => (
                            <Cell key={entry.id} fill={chartPalette[index % chartPalette.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name, payload) => [
                            `${Number(value || 0).toLocaleString('en-IN')} qty`,
                            `${name} (${Number(payload?.payload?.percentage || 0).toFixed(1)}%)`,
                          ]}
                        />
                        <Legend {...chartLegendProps} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ color: themeColors.subtleText, fontSize: '0.86rem' }}>
                      No purchase product data available for selected range.
                    </Typography>
                  </Box>
                )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={chartCardSx(themeColors.success)}
                >
                <Typography sx={{ color: themeColors.text, fontWeight: 700, mb: 0.4 }}>
                  Current Stock Snapshot
                </Typography>
                <Typography sx={{ color: themeColors.subtleText, fontSize: '0.8rem', mb: 1.5 }}>
                  Real-time quantity across latest stock items
                </Typography>
                {dashboardBusy ? (
                  <ChartAreaSkeleton isDark={isDark} height={300} />
                ) : currentStockSnapshotChartData.length ? (
                  <Box sx={chartPlotSx(300)}>
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={300}>
                      <BarChart
                        data={currentStockSnapshotChartData}
                        layout="vertical"
                        margin={chartMarginLegend}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={themeColors.surfaceBorder} opacity={isDark ? 0.2 : 0.3} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: themeColors.text }} stroke={themeColors.surfaceBorder} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={110}
                          tick={{ fontSize: 11, fill: themeColors.text }}
                          stroke={themeColors.surfaceBorder}
                        />
                        <Tooltip
                          formatter={(value, key) => [
                            Number(value || 0).toLocaleString('en-IN'),
                            key === 'current_qty' ? 'Current Qty' : key,
                          ]}
                        />
                        <Legend {...chartLegendProps} />
                        <Bar dataKey="current_qty" name="Current Qty" fill={themeColors.success} radius={[4, 4, 4, 4]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Typography sx={{ color: themeColors.subtleText, fontSize: '0.86rem' }}>
                    No current stock snapshot available.
                  </Typography>
                )}
                </Paper>
              </Grid>
            </Grid>
            </Box>
          )}

          {showAlertsPanel && (
            <Box sx={dashboardSectionSx}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={chartCardSx(themeColors.danger)}
                >
                <Typography variant="h6" sx={{ color: themeColors.text, fontWeight: 600, mb: 0.5 }}>
                  Low Stock Alert Trend
                </Typography>
                <Typography variant="body2" sx={{ color: themeColors.subtleText, mb: 1.8 }}>
                  Date-wise low stock signal and wastage trend ({monthRangeLabel})
                </Typography>
                {dashboardBusy ? (
                  <ChartAreaSkeleton isDark={isDark} height={320} />
                ) : (
                <Box sx={chartPlotSx(320)}>
                  <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={320}>
                    <BarChart data={monthLowStockTrendData} margin={chartMarginLegend}>
                      <CartesianGrid strokeDasharray="3 3" stroke={themeColors.surfaceBorder} opacity={isDark ? 0.2 : 0.3} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: themeColors.text }} stroke={themeColors.surfaceBorder} />
                      <YAxis tick={{ fontSize: 11, fill: themeColors.text }} stroke={themeColors.surfaceBorder} />
                      <Tooltip />
                      <Legend {...chartLegendProps} />
                      <Bar dataKey="low_stock_signal" name="Low Stock Signal" fill={themeColors.warning} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="wastage" name="Wastage" fill={themeColors.danger} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
                )}
                </Paper>
              </Grid>
            </Grid>
            </Box>
          )}
        </>
      )}
      </Box>
        </>
      )}

      {activeTab === 1 && (
        <StockInventoryListSection
          isDark={isDark}
          onAddInventory={() => navigate('/stock/?tab=add-stock')}
          onEditInventory={(inventoryId) => navigate(`/stock/?tab=add-stock&mode=edit&id=${inventoryId}`)}
          onViewInventory={(inventoryId) => navigate(`/stock/?tab=add-stock&mode=view&id=${inventoryId}`)}
        />
      )}

      {activeTab === 2 && (
        <StockAddInventorySection
          isDark={isDark}
          mode={inventoryMode}
          inventoryId={selectedInventoryId}
          onSaved={() => navigate('/stock/?tab=stock-list')}
          onCancel={() => navigate('/stock/?tab=stock-list')}
        />
      )}

      {activeTab === 3 && <StockOutSection isDark={isDark} />}

      {activeTab === 4 && <StockSupplierSection isDark={isDark} />}

      {activeTab === 5 && <StockRawMaterialSection isDark={isDark} />}

      {activeTab === 6 && <StockReportsSection isDark={isDark} />}
    </Box>
  );
};

export default InventoryOperationsDashboardPage;
